import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';

import {
  selectChevronClassesExperimental,
  selectItemVariantsExperimental,
  selectLabelClassesExperimental,
  selectSeparatorClassesExperimental,
  selectTriggerVariantsExperimental,
} from './select.experimental.styles';
import {
  selectContentPopperVariants,
  selectContentVariants,
  selectContentViewportClasses,
  selectContentViewportPopperClasses,
  selectItemVariants,
  selectLabelVariants,
  selectScrollButtonClasses,
  selectSeparatorClasses,
  selectTriggerVariants,
} from './select.styles';
import type {
  SelectDensity,
  SelectItemProps,
  SelectProps,
  SelectTriggerProps,
} from './select.types';

const SelectDensityContext = React.createContext<SelectDensity>('comfortable');

// Keys Radix uses to open the listbox from the trigger. Mirrors the OPEN_KEYS
// list in @radix-ui/react-select — these must reach Radix's own keydown handler.
const RADIX_OPEN_KEYS = [' ', 'Enter', 'ArrowUp', 'ArrowDown'];

/**
 * @remarks Prefer combobox.tsx for new features, unless you need minimal render overhead.
 * [see design system figma](https://www.figma.com/design/h1UlOqWB5isrcf6VTsYWGb/Heidi-Design-System--Shadcn-?node-id=4824-81092&p=f&m=dev)
 */
const Select = ({ density = 'comfortable', children, ...props }: SelectProps) => (
  <SelectDensityContext.Provider value={density}>
    <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>
  </SelectDensityContext.Provider>
);

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(({ className, children, hideSelectIcon, icon, density, onKeyDown, ...props }, ref) => {
  const contextDensity = React.useContext(SelectDensityContext);
  const resolvedDensity = density ?? contextDensity;
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  const triggerVariants = isExperimentalForms
    ? selectTriggerVariantsExperimental
    : selectTriggerVariants;
  return (
    <SelectPrimitive.Trigger
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        // Let Tab/Escape, Radix's open keys, and typeahead chars through:
        // preventDefault here would stop Radix's own keydown handler
        // (composeEventHandlers skips it once defaultPrevented), breaking
        // keyboard open/search.
        if (e.key === 'Tab' || e.key === 'Escape') return;
        if (RADIX_OPEN_KEYS.includes(e.key)) return;
        const isModifierKey = e.ctrlKey || e.altKey || e.metaKey;
        if (!isModifierKey && e.key.length === 1) return;
        e.preventDefault();
      }}
      ref={ref}
      data-density={resolvedDensity}
      data-experimental={isExperimentalForms || undefined}
      className={cn(triggerVariants({ density: resolvedDensity }), className)}
      {...props}
    >
      {children}
      {!hideSelectIcon && (
        <SelectPrimitive.Icon asChild>
          {icon || (
            <ChevronDown
              className={cn(
                'opacity-50',
                'shrink-0',
                isExperimentalForms && selectChevronClassesExperimental,
                resolvedDensity === 'compact'
                  ? isExperimentalForms
                    ? 'size-3.5'
                    : 'size-3'
                  : 'size-4'
              )}
            />
          )}
        </SelectPrimitive.Icon>
      )}
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(selectScrollButtonClasses, className)}
    {...props}
  >
    <ChevronUp className="size-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(selectScrollButtonClasses, className)}
    {...props}
  >
    <ChevronDown className="size-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', align = 'center', ...props }, ref) => {
  const density = React.useContext(SelectDensityContext);
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        align={align}
        data-density={density}
        className={cn(
          selectContentVariants({ density }),
          position === 'popper' && selectContentPopperVariants(),
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            selectContentViewportClasses,
            position === 'popper' && selectContentViewportPopperClasses
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => {
  const density = React.useContext(SelectDensityContext);
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <SelectPrimitive.Label
      ref={ref}
      data-density={density}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? selectLabelClassesExperimental
          : selectLabelVariants({ density }),
        className
      )}
      {...props}
    />
  );
});
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  SelectItemProps
>(({ className, children, hideCheckIcon = false, value, ...props }, ref) => {
  const density = React.useContext(SelectDensityContext);
  const isCompact = density === 'compact';
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  const itemVariants = isExperimentalForms
    ? selectItemVariantsExperimental
    : selectItemVariants;

  // Prevent empty string values which cause runtime errors
  const safeValue = value === '' ? 'no-option-selected' : value;

  // The check indicator is always end-aligned; padding leaves room on the end
  // side for it (or collapses to symmetric px-2 when the check is hidden).
  const paddingClasses = hideCheckIcon ? 'px-2' : isCompact ? 'ps-2 pe-6' : 'ps-2 pe-8';

  return (
    <SelectPrimitive.Item
      ref={ref}
      data-density={density}
      data-experimental={isExperimentalForms || undefined}
      data-no-check={hideCheckIcon || undefined}
      className={cn(paddingClasses, itemVariants({ density }), className)}
      value={safeValue}
      {...props}
    >
      {!hideCheckIcon && (
        <span className={cn('end-2', 'absolute flex items-center justify-center')}>
          <SelectPrimitive.ItemIndicator>
            <Check
              className={
                isCompact ? (isExperimentalForms ? 'size-3.5' : 'size-3') : 'size-4'
              }
            />
          </SelectPrimitive.ItemIndicator>
        </span>
      )}

      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <SelectPrimitive.Separator
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms ? selectSeparatorClassesExperimental : selectSeparatorClasses,
        className
      )}
      {...props}
    />
  );
});
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
